import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { notificationApi } from '@/services/notifications';
import { useAuth } from '@/hooks/useAuth';
import type { Notification } from '@/types/notification';
import { getAdminBadgeCounts } from '@/services/adminPageService';
import type { AdminBadgeCounts } from '@/services/adminPageService';

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  adminBadgeCounts: AdminBadgeCounts;
  isLoading: boolean;
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

const POLL_INTERVAL = 30_000; // 30 seconds

interface NotificationProviderProps {
  children: ReactNode;
  // Optional seed values from session-init to skip the initial fetch
  initialUnreadCount?: number;
  initialAdminBadgeCounts?: AdminBadgeCounts | null;
}

export function NotificationProvider({
  children,
  initialUnreadCount,
  initialAdminBadgeCounts,
}: NotificationProviderProps) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const userRole = user?.role ?? null;
  const identity = `${userId}:${userRole}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const scopeRef = useRef({ identity, controller: new AbortController(), unreadBusy: false, badgesBusy: false, listBusy: false });
  const previousIdentityRef = useRef(identity);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  // Use seed values when provided to avoid an initial fetch
  const [unreadCount, setUnreadCount] = useState(initialUnreadCount ?? 0);
  const [adminBadgeCounts, setAdminBadgeCounts] = useState<AdminBadgeCounts>(
    initialAdminBadgeCounts ?? { pending_refunds: 0, unread_chats: 0 }
  );
  const [isLoading, setIsLoading] = useState(false);

  // Track whether the initial seed data was used so we can skip the first poll fetch
  const hasInitialSeed = useRef(
    initialUnreadCount !== undefined || initialAdminBadgeCounts !== undefined
  );

  useEffect(() => {
    const scope = { identity, controller: new AbortController(), unreadBusy: false, badgesBusy: false, listBusy: false };
    scopeRef.current = scope;
    if (previousIdentityRef.current !== identity || userId === null) {
      setNotifications([]);
      setUnreadCount(0);
      setAdminBadgeCounts({ pending_refunds: 0, unread_chats: 0 });
      setIsLoading(false);
    }
    previousIdentityRef.current = identity;
    return () => scope.controller.abort();
  }, [identity, userId]);

  const fetchUnreadCount = useCallback(async () => {
    const scope = scopeRef.current;
    if (userId === null || scope.identity !== identity || scope.unreadBusy || scope.controller.signal.aborted || document.visibilityState === 'hidden') return;
    scope.unreadBusy = true;
    try {
      const { data } = await notificationApi.getUnreadCount(scope.controller.signal);
      if (!scope.controller.signal.aborted && identityRef.current === identity) setUnreadCount(data.unread_count);
    } catch {
      // Silently ignore — polling will retry
    } finally {
      scope.unreadBusy = false;
    }
  }, [userId, identity]);

  const fetchAdminBadges = useCallback(async () => {
    const scope = scopeRef.current;
    if (userId === null || scope.identity !== identity || scope.badgesBusy || scope.controller.signal.aborted || document.visibilityState === 'hidden') return;
    if (userRole !== 'administrator') {
      setAdminBadgeCounts({ pending_refunds: 0, unread_chats: 0 });
      return;
    }
    scope.badgesBusy = true;
    try {
      const data = await getAdminBadgeCounts(scope.controller.signal);
      if (!scope.controller.signal.aborted && identityRef.current === identity) setAdminBadgeCounts(data);
    } catch {
      // Silently ignore
    } finally {
      scope.badgesBusy = false;
    }
  }, [userId, userRole, identity]);

  const fetchNotifications = useCallback(async () => {
    const scope = scopeRef.current;
    if (userId === null || scope.identity !== identity || scope.listBusy || scope.controller.signal.aborted || document.visibilityState === 'hidden') return;
    scope.listBusy = true;
    setIsLoading(true);
    try {
      const { data } = await notificationApi.getNotifications(20, 0, scope.controller.signal);
      if (!scope.controller.signal.aborted && identityRef.current === identity) setNotifications(data.notifications);
    } catch {
      // Silently ignore
    } finally {
      scope.listBusy = false;
      if (!scope.controller.signal.aborted && identityRef.current === identity) setIsLoading(false);
    }
  }, [userId, identity]);

  const markAsRead = useCallback(async (id: number) => {
    const scope = scopeRef.current;
    try {
      await notificationApi.markAsRead(id);
      if (scope.controller.signal.aborted || scope.identity !== identityRef.current) return;
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Silently ignore
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const scope = scopeRef.current;
    try {
      await notificationApi.markAllAsRead();
      if (scope.controller.signal.aborted || scope.identity !== identityRef.current) return;
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // Silently ignore
    }
  }, []);

  const deleteNotification = useCallback(async (id: number) => {
    const scope = scopeRef.current;
    try {
      await notificationApi.deleteNotification(id);
      if (scope.controller.signal.aborted || scope.identity !== identityRef.current) return;
      setNotifications((prev) => {
        const target = prev.find((n) => n.id === id);
        if (target && !target.is_read) {
          setUnreadCount((c) => Math.max(0, c - 1));
        }
        return prev.filter((n) => n.id !== id);
      });
    } catch {
      // Silently ignore
    }
  }, []);

  const deleteAllNotifications = useCallback(async () => {
    const scope = scopeRef.current;
    try {
      await notificationApi.deleteAll();
      if (scope.controller.signal.aborted || scope.identity !== identityRef.current) return;
      setNotifications([]);
      setUnreadCount(0);
    } catch {
      // Silently ignore
    }
  }, []);

  // Focus, visibility changes and interval ticks share the same in-flight guards.
  useEffect(() => {
    if (userId === null) return;
    const poll = () => {
      if (document.visibilityState === 'hidden') return;
      void fetchUnreadCount();
      void fetchAdminBadges();
    };
    if (hasInitialSeed.current) {
      hasInitialSeed.current = false;
    } else {
      poll();
    }
    const interval = window.setInterval(poll, POLL_INTERVAL);
    window.addEventListener('focus', poll);
    document.addEventListener('visibilitychange', poll);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', poll);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [userId, fetchUnreadCount, fetchAdminBadges]);

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount,
      adminBadgeCounts,
      isLoading,
      fetchNotifications,
      fetchUnreadCount,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      deleteAllNotifications,
    }),
    [notifications, unreadCount, adminBadgeCounts, isLoading, fetchNotifications, fetchUnreadCount, markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
